import { useState, useEffect, useCallback, useRef } from 'react';

// Tipos para la API de reconocimiento de voz
interface SpeechRecognitionOptions {
  lang?: string;
  continuous?: boolean;
  interimResults?: boolean;
  maxAlternatives?: number;
}

interface SpeechRecognitionState {
  isListening: boolean;
  transcript: string;
  interimTranscript: string;
  isSupported: boolean;
  error: string | null;
}

interface SpeechRecognitionActions {
  startListening: (initialTranscript?: string) => void;
  stopListening: () => void;
  resetTranscript: () => void;
}

// Constructor de SpeechRecognition
type SpeechRecognitionConstructor = {
  prototype: SpeechRecognition;
  new (): SpeechRecognition;
};

interface SpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  maxAlternatives: number;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
  onstart: (() => void) | null;
}

interface SpeechRecognitionEvent {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionResultList {
  length: number;
  item(index: number): SpeechRecognitionResult;
  [index: number]: SpeechRecognitionResult;
}

interface SpeechRecognitionResult {
  length: number;
  item(index: number): SpeechRecognitionAlternative;
  [index: number]: SpeechRecognitionAlternative;
  isFinal: boolean;
}

interface SpeechRecognitionAlternative {
  transcript: string;
  confidence: number;
}

interface SpeechRecognitionErrorEvent {
  error: string;
  message: string;
}

// Declaración global de Window
declare global {
  interface Window {
    SpeechRecognition: SpeechRecognitionConstructor;
    webkitSpeechRecognition: SpeechRecognitionConstructor;
  }
}

// KAN-225 diagnóstico: numerador de sesiones para seguir cada escucha en consola.
let dictationSessionCounter = 0;

export const useSpeechRecognition = (options: SpeechRecognitionOptions = {}): SpeechRecognitionState & SpeechRecognitionActions => {
  const {
    lang = 'es-ES',
    continuous = false,
    interimResults = true,
    maxAlternatives = 1,
  } = options;

  const [isListening, setIsListening] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [interimTranscript, setInterimTranscript] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isSupported] = useState(() => {
    const w = window as unknown as { SpeechRecognition?: unknown; webkitSpeechRecognition?: unknown };
    return !!(w.SpeechRecognition || w.webkitSpeechRecognition);
  });

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  // Guard anti-zombie: sesión viva como objeto capturado en el closure de los
  // handlers de CADA escucha. Cuando la sesión se cierra (onend, abort del
  // watchdog, arranque de otra escucha, reset o unmount) se marca `closed` y
  // los eventos tardíos se ignoran. En algunos dispositivos el navegador no
  // honra stop()/abort() y sigue entregando resultados diferidos de una
  // sesión que el usuario ya frenó (bug: "el micrófono seguía escribiendo
  // solo y al borrar el texto se re-llenaba con todo lo hablado").
  const activeSessionRef = useRef<{ closed: boolean } | null>(null);
  // KAN-225: si el reconocedor no dispara onend tras stop() (bug de Chrome
  // visto en dispositivo real), este watchdog mata la sesión con abort()
  // para liberar el micrófono.
  const stopWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const finalTranscriptRef = useRef('');
  // Último interim visto (texto tentativo aún no finalizado). Se rescata en
  // onend: cuando el navegador corta por silencio (o el usuario suelta el
  // mic), el fragmento hablado pero no confirmado se perdía — el input se
  // quedaba con menos texto del dictado (síntoma: "borra lo último que dije").
  const interimRef = useRef('');
  // Texto que ya estaba en el input al arrancar a escuchar, sobre el que se
  // "sigue dictando". Se mantiene separado del transcript que se reconstruye en
  // onresult para poder recalcular desde event.results sin perder la base.
  const baseTranscriptRef = useRef('');

  // KAN-225 (InvalidStateError): cada ESCUCHA crea una instancia nueva de
  // SpeechRecognition con la config vigente. Re-usar una instancia longeva
  // rompía cuando el teardown de la sesión anterior no completaba (start()
  // sobre una sesión "already started").
  const configRef = useRef({ lang, continuous, interimResults, maxAlternatives });
  configRef.current = { lang, continuous, interimResults, maxAlternatives };

  // Cierre total de una sesión: marca el guard y ANULA los handlers de la
  // instancia. Con las dos capas, ni un feed zombie del navegador (uno que
  // ignore abort() y siga disparando onresult) puede volver a tocar estado.
  const closeSession = useCallback((rec: SpeechRecognition | null) => {
    if (activeSessionRef.current) activeSessionRef.current.closed = true;
    if (rec) {
      rec.onresult = null;
      rec.onerror = null;
      rec.onend = null;
    }
  }, []);

  const createRecognition = useCallback((): SpeechRecognition | null => {
    const SpeechRecognitionAPI = (window.SpeechRecognition || window.webkitSpeechRecognition) as SpeechRecognitionConstructor | undefined;
    if (!SpeechRecognitionAPI) return null;

    const cfg = configRef.current;
    const sessionId = ++dictationSessionCounter;
    // Sesión viva de ESTA escucha: viaja en el closure de los handlers. Al
    // cerrarse (onend / watchdog / otra escucha / reset / unmount) el objeto
    // se marca y todo evento tardío se descarta.
    const session = { closed: false };
    activeSessionRef.current = session;
    const recognition = new SpeechRecognitionAPI();
    recognition.continuous = cfg.continuous;
    recognition.interimResults = cfg.interimResults;
    recognition.lang = cfg.lang;
    recognition.maxAlternatives = cfg.maxAlternatives;
    console.log(`[Dictado] sesión #${sessionId} creada`, {
      lang: cfg.lang,
      continuous: cfg.continuous,
      modo: 'cloud',
    });

    recognition.onresult = (event: SpeechRecognitionEvent) => {
      if (session.closed) {
        console.warn(`[Dictado] sesión #${sessionId} onresult ignorado (sesión cerrada)`);
        return;
      }
      // Reconstruimos el transcript desde el array CUMULATIVO event.results
      // (desde el índice 0), NO desde event.resultIndex ni appendando con `+=`.
      // En mobile Chrome resultIndex no avanza y los finales ya confirmados se
      // re-entregan en cada evento; acumular con `+=` duplicaba el texto
      // ("el el el campo el campo…"). Recalcular desde la fuente de verdad es
      // inmune a ese comportamiento (KAN-178 / KAN-179).
      let sessionFinal = '';
      let interimText = '';

      for (let i = 0; i < event.results.length; i++) {
        const result = event.results[i];
        const alternative = result.item(0);

        if (result.isFinal) {
          sessionFinal += alternative.transcript + ' ';
        } else {
          interimText += alternative.transcript;
        }
      }

      finalTranscriptRef.current = baseTranscriptRef.current + sessionFinal;
      setTranscript(finalTranscriptRef.current);
      interimRef.current = interimText;
      setInterimTranscript(interimText);
      console.log(`[Dictado] sesión #${sessionId} onresult`, {
        resultados: event.results.length,
        transcript: finalTranscriptRef.current.slice(-60),
      });
    };

    recognition.onerror = (event: SpeechRecognitionErrorEvent) => {
      if (session.closed) {
        console.warn(`[Dictado] sesión #${sessionId} onerror ignorado (sesión cerrada)`);
        return;
      }
      console.error(`[Dictado] sesión #${sessionId} onerror:`, event.error, event.message);
      setError(event.message || event.error);
      setIsListening(false);

      // Mostrar error específico al usuario
      if (event.error === 'not-allowed') {
        setError('Permiso de micrófono denegado. Habilita el micrófono en tu navegador.');
      } else if (event.error === 'no-speech') {
        setError('No se detectó voz. Probá de nuevo.');
      } else if (event.error === 'audio-capture') {
        setError('No se pudo capturar audio. Revisá tu micrófono.');
      } else if (event.error === 'network') {
        setError('Error de red en el reconocimiento de voz.');
      }
    };

    // Push-to-talk: cuando el navegador corta (silencio, fin, tope de tiempo)
    // simplemente dejamos de escuchar. NO se reinicia automáticamente: la
    // escucha queda acotada a la pulsación del botón. Reabrir el micrófono
    // sobre ruido ambiente era lo que causaba el "escribe boludeces".
    recognition.onend = () => {
      console.log(`[Dictado] sesión #${sessionId} onend`);
      // Primero cerrar la sesión: lo que llegue después de onend se ignora.
      session.closed = true;
      if (stopWatchdogRef.current) {
        clearTimeout(stopWatchdogRef.current);
        stopWatchdogRef.current = null;
      }
      // Rescate del interim: el fragmento hablado que Chrome aún no había
      // marcado como final se perdía al cortar (por silencio o por soltar
      // el mic). Se suma al transcript para no perder lo dictado.
      const pendiente = interimRef.current.trim();
      if (pendiente) {
        console.log(`[Dictado] sesión #${sessionId} rescate de interim: "${pendiente.slice(-60)}"`);
        finalTranscriptRef.current = (finalTranscriptRef.current + ' ' + pendiente).trim();
        setTranscript(finalTranscriptRef.current);
      }
      interimRef.current = '';
      setInterimTranscript('');
      recognitionRef.current = null; // sesión cerrada
      setIsListening(false);
    };

    recognition.onstart = () => {
      console.log(`[Dictado] sesión #${sessionId} onstart`);
      setIsListening(true);
      setError(null);
    };

    return recognition;
  }, []);

  // Cleanup al desmontar: matar sesión (guard + handlers + abort) y watchdog.
  useEffect(() => {
    return () => {
      if (stopWatchdogRef.current) {
        clearTimeout(stopWatchdogRef.current);
        stopWatchdogRef.current = null;
      }
      if (recognitionRef.current) {
        closeSession(recognitionRef.current);
        try {
          recognitionRef.current.abort();
        } catch {
          // Ignore
        }
        recognitionRef.current = null;
      }
    };
  }, [closeSession]);

  const startListening = useCallback((initialTranscript?: string) => {
    // KAN-225: instancia NUEVA por escucha. Si quedó una sesión colgada (el
    // browser no completó el teardown), se mata antes — evita el
    // InvalidStateError "recognition has already started".
    if (recognitionRef.current) {
      console.log('[Dictado] start(): abortando sesión previa (colgada o activa)');
      closeSession(recognitionRef.current);
      try {
        recognitionRef.current.abort();
      } catch {
        /* ya muerta */
      }
      recognitionRef.current = null;
    }

    const recognition = createRecognition();
    if (!recognition) {
      setError('Reconocimiento de voz no disponible');
      return;
    }

    try {
      // Base sobre la que se sigue dictando: el texto pasado (continuación) o lo
      // ya acumulado. onresult reconstruye el transcript como base + finales de
      // esta sesión, así que acá solo fijamos la base.
      const base = initialTranscript !== undefined ? initialTranscript : finalTranscriptRef.current;
      baseTranscriptRef.current = base;
      finalTranscriptRef.current = base;
      setTranscript(base);
      // Interim a cero también en el ref: un interim de una sesión muerta no
      // puede ser "rescatado" por el onend de ESTA sesión nueva.
      interimRef.current = '';
      setInterimTranscript('');
      setError(null);
      recognitionRef.current = recognition;
      recognition.start();
    } catch (e) {
      console.error('Error starting speech recognition:', e);
      recognitionRef.current = null;
      setError('Error al iniciar reconocimiento de voz');
    }
  }, [createRecognition, closeSession]);

  const stopListening = useCallback(() => {
    // Detención explícita (soltar botón / tap). KAN-225: NO dependemos de que
    // el browser dispare onend — Chrome puede quedarse finalizando resultados
    // y isListening quedaba trabado. Bajamos el estado acá y un watchdog
    // aborta la sesión si no corta en ~1s.
    // IMPORTANTE: el watchdog captura ESTA instancia — abortar
    // recognitionRef.current directo mataba la sesión NUEVA si el usuario
    // volvía a apretar antes de que el timer disparara.
    const rec = recognitionRef.current;
    setIsListening(false);
    if (!rec) return;
    try {
      rec.stop();
    } catch {
      /* ya estaba cerrada */
    }
    if (stopWatchdogRef.current) clearTimeout(stopWatchdogRef.current);
    stopWatchdogRef.current = setTimeout(() => {
      console.warn('[Dictado] watchdog: onend no llegó — abort de la sesión colgada');
      // Cierre total: guard + handlers anulados + abort. Aunque el navegador
      // ignore el abort() y siga disparando onresult, la sesión ya está
      // cerrada para nosotros. Limpieza tipo onend SIN rescate de interim:
      // el interim del zombie es audio que el usuario ya frenó, se descarta.
      closeSession(rec);
      try {
        rec.abort();
      } catch {
        /* best-effort */
      }
      interimRef.current = '';
      setInterimTranscript('');
      if (recognitionRef.current === rec) recognitionRef.current = null;
    }, 1000);
  }, [closeSession]);

  const resetTranscript = useCallback(() => {
    // Reset = el usuario toma el control (envió el mensaje o editó el texto a
    // mano): la sesión activa se mata entera — guard + handlers + abort —
    // para que NADA de esa escucha pueda re-aterrizar en el input después.
    if (stopWatchdogRef.current) {
      clearTimeout(stopWatchdogRef.current);
      stopWatchdogRef.current = null;
    }
    if (recognitionRef.current) {
      closeSession(recognitionRef.current);
      try {
        recognitionRef.current.abort();
      } catch {
        /* ya muerta */
      }
      recognitionRef.current = null;
    }
    baseTranscriptRef.current = '';
    finalTranscriptRef.current = '';
    interimRef.current = '';
    setTranscript('');
    setInterimTranscript('');
    setError(null);
  }, [closeSession]);

  return {
    isListening,
    transcript,
    interimTranscript,
    isSupported,
    error,
    startListening,
    stopListening,
    resetTranscript,
  };
};
